import { useTheme } from "@/hooks/useTheme";

const steps = [
  { num: "01", text: "Book your system audit" },
  { num: "02", text: "We map your current process" },
  { num: "03", text: "We identify revenue leaks" },
  { num: "04", text: "We design your integration strategy" },
  { num: "05", text: "We deploy and optimize your system" },
];

export default function ContactSection() {
  const { isDark } = useTheme();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const bg = isDark ? "#1C1A22" : "#FBF6EE";
  const headlineColor = isDark ? "white" : "#1C1A22";
  const mutedColor = isDark ? "rgba(255,255,255,0.38)" : "rgba(28,26,34,0.45)";
  const bodyColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(28,26,34,0.55)";
  const stepDivider = isDark ? "rgba(255,255,255,0.05)" : "rgba(28,26,34,0.07)";
  const stepArrow = isDark ? "rgba(255,255,255,0.2)" : "rgba(28,26,34,0.2)";
  const floatingCardBg = isDark ? "rgba(28,26,34,0.88)" : "rgba(251,246,238,0.92)";
  const floatingCardBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(28,26,34,0.1)";
  const gradientFrom = isDark ? "#1C1A22" : "#FBF6EE";
  const dividerGradient = isDark
    ? "linear-gradient(90deg, transparent, rgba(90,123,168,0.3), rgba(232,118,26,0.2), transparent)"
    : "linear-gradient(90deg, transparent, rgba(90,123,168,0.35), rgba(232,118,26,0.25), transparent)";

  return (
    <section id="contact" className="py-28 md:py-36 relative overflow-hidden transition-colors duration-500" style={{ background: bg }}>
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{ background: isDark ? "radial-gradient(circle, rgba(90,123,168,0.06) 0%, transparent 70%)" : "radial-gradient(circle, rgba(90,123,168,0.05) 0%, transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="h-px w-full" style={{ background: dividerGradient }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-medium mb-6" style={{ color: "#5A7BA8" }}>Let&apos;s Connect</p>
            <h2 className="text-4xl md:text-6xl font-thin leading-tight mb-8 tracking-tight transition-colors duration-500" style={{ color: headlineColor }}>
              Turn Your Operations
              <br />
              <span className="font-semibold">Into a System.</span>
            </h2>
            <p className="text-base leading-relaxed mb-3 font-light transition-colors duration-500" style={{ color: mutedColor }}>
              If your business is dealing with missed calls, slow follow-ups, or disconnected tools — it&apos;s not a staffing problem.
            </p>
            <p className="text-base leading-relaxed mb-12 font-light transition-colors duration-500" style={{ color: bodyColor }}>
              It&apos;s a <span style={{ color: headlineColor }} className="font-normal">systems problem.</span> We analyze your current workflow, identify gaps, and implement a fully integrated system.
            </p>

            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] mb-6" style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(28,26,34,0.25)" }}>What Happens Next</p>
              {steps.map((step, i) => (
                <div
                  key={step.num}
                  className="flex items-center gap-5 py-4"
                  style={{ borderBottom: i < steps.length - 1 ? `1px solid ${stepDivider}` : "none" }}
                >
                  <span className="text-xs font-mono font-bold w-7 flex-shrink-0" style={{ color: "rgba(232,118,26,0.5)" }}>{step.num}</span>
                  <span className="text-sm font-light transition-colors duration-500" style={{ color: bodyColor }}>{step.text}</span>
                  <span className="ml-auto w-4 h-4 flex items-center justify-center" style={{ color: stepArrow }}>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <p className="text-sm font-light mb-1 transition-colors duration-500" style={{ color: mutedColor }}>Simple. Structured. Results-driven.</p>
              <p className="font-medium text-base transition-colors duration-500" style={{ color: headlineColor }}>Stop losing revenue to broken processes.</p>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/0a4af141f9cecabdd0d17c6f7bab9bb9.jpg"
                alt="Connected Systems"
                className="w-full h-[480px] md:h-[580px] object-cover object-top"
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(to top, ${gradientFrom} 0%, ${gradientFrom}26 50%, transparent 100%)` }}
              />
            </div>

            <div
              className="absolute bottom-8 left-6 right-6 backdrop-blur-xl rounded-2xl p-6 transition-all duration-500"
              style={{ background: floatingCardBg, border: `1px solid ${floatingCardBorder}` }}
            >
              <p className="font-semibold text-base mb-1 transition-colors duration-500" style={{ color: headlineColor }}>Start operating as a connected system.</p>
              <p className="text-sm mb-5 font-light transition-colors duration-500" style={{ color: mutedColor }}>Every interaction captured, processed, and acted on instantly.</p>
              <button
                onClick={() => scrollTo("book-audit")}
                className="w-full py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{ background: "linear-gradient(135deg, #E8761A, #F5C842)" }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.9")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
              >
                Book Your System Audit Today
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
